/**
 * Who is signed in, from `/auth/me`, and what the policy lets them reach. The nav is derived
 * here from the registry with `can()` — the same function the API guard runs — so a section
 * is drawn only when a door in it is open to this person.
 */
import { useCallback, useEffect, useState } from 'react';
import { CAPABILITIES, can, role } from '@cider/domain/access';
import type { Capability, Person, RoleId, Scope } from '@cider/domain/access';
import type { ViewId } from '@cider/ui';
import { api, isRefused } from './api.js';
import { RAILS, railsOf, type Section } from './nav.js';

export interface MeRole {
  role: RoleId;
  festivalYear: number;
  areas: string[];
  /** The areas by name, in the same order (People Part 5). */
  areaNames?: string[];
  chairKind: string | null;
  /** False with `blocked` set when compliance is unmet (People Part 2). */
  active?: boolean;
  blocked?: string | null;
}

export interface Me {
  signedIn: boolean;
  kind?: 'person' | 'device';
  personId?: string;
  email?: string;
  /** What the festival calls this person; the bar says the name, and the address when it has none. */
  name?: string;
  /** The other chairs on this person's areas, by name — the chair app's header says them. */
  coChairs?: string[];
  roles?: MeRole[];
  views?: ViewId[];
  home?: string;
  secondFactor?: 'ok' | 'pending' | 'set_one';
  /** Which kinds the account actually holds — the second step draws only these (Sep 25). */
  factorsSet?: { passkey: boolean; totp: boolean };
  mustSetSecondFactor?: boolean;
  /** The first sign-in's card (People Part 5): due until seen. */
  onboarding?: 'due' | 'done';
  /** The last version whose What's new this account opened (People Part 6). */
  seenVersion?: string | null;
  /** `area` is the area's id (the policy's scope); `areaName` is what a screen prints. */
  viewingAs?: {
    chairPersonId: string;
    area: string;
    areaName?: string | null;
    chairName?: string | null;
    until: string | null;
    /** The year of the seat being looked through. */
    festivalYear?: number | null;
  } | null;
  session?: { id: string; lane: string; expiresAt: string };
  viewedAsMe?: { at: string; actorId: string | null; detail: Record<string, unknown> }[];
  device?: { id: string; label: string; kind: string; state: string };
}

export function personOf(me: Me): Person {
  const p: Person = {
    id: me.personId ?? me.device?.id ?? 'nobody',
    secondFactorSet: me.secondFactor === 'ok',
    assignments: (me.roles ?? []).map((r) => ({
      role: r.role,
      festivalYear: r.festivalYear,
      areas: r.areas,
      active: r.active ?? true,
      ...(r.blocked ? { blocked: r.blocked } : {}),
      ...(r.chairKind ? { chairKind: r.chairKind as never } : {}),
    })),
  };
  if (me.kind === 'device' && me.device) {
    p.deviceId = me.device.id;
    p.assignments = [{ role: 'device', festivalYear: 0, active: true }];
  }
  if (me.viewingAs)
    p.viewingAs = { chairPersonId: me.viewingAs.chairPersonId, area: me.viewingAs.area };
  return p;
}

/** Can this person open this door anywhere they are assigned? A read, in their own year and area. */
export function reaches(person: Person, cap: Capability): boolean {
  const first = person.assignments[0];
  const area = person.assignments.flatMap((a) => a.areas ?? [])[0];
  const scope: Scope = { ownerId: person.id };
  if (first) scope.festivalYear = first.festivalYear;
  if (area) scope.area = area;
  if (person.deviceId) scope.deviceId = person.deviceId;
  return can(person, cap.id, scope).allowed;
}

/**
 * The sections of a view this person can reach, in the canvas's order — and, inside a section,
 * only the screens their doors open. A capability that names `screens` opens those and no
 * others (ADR 0016); one that names none opens the whole section, as every door did before.
 * The rail is the union over the doors they hold in that section.
 */
export function sectionsFor(
  person: Person,
  view: ViewId,
  opts: { byAddress?: boolean } = {},
): readonly Section[] {
  const open = CAPABILITIES.filter((c) => c.view === view && reaches(person, c));
  const titles = new Set(open.map((c) => c.section));
  // ADR 0033: the school's ledger is the school role's own rail. A school person's rail is that
  // rail (their one door opens nothing else); everyone else's Volunteers rail leaves it out, and
  // an address still opens it for whoever holds its door.
  const school = person.assignments.some((a) => a.role === 'school' && a.active);
  return (school || opts.byAddress ? railsOf(view) : RAILS[view])
    .filter((s) => titles.has(s.title))
    .map((s) => {
      const mine = open.filter((c) => c.section === s.title);
      if (mine.some((c) => !c.screens)) return s;
      const ids = new Set(mine.flatMap((c) => c.screens ?? []));
      return { ...s, screens: s.screens.filter((x) => ids.has(x.id)) };
    })
    .filter((s) => s.screens.length > 0);
}

/**
 * **Count room first during festival week** (James, Oct 4): in setup week and on the festival's
 * days, Money's rail opens on Cash · Bank with the count room at its head; every other week it
 * is as drawn. Only the order moves — nothing is added or taken away.
 */
export function inFestivalWeekOrder(
  sections: readonly Section[],
  view: ViewId,
  phase: string | null,
): readonly Section[] {
  if (view !== 'money' || (phase !== 'setup' && phase !== 'day' && phase !== 'festival'))
    return sections;
  const cash = sections.find((s) => s.title === 'Cash · Bank');
  if (!cash) return sections;
  const room = cash.screens.filter((x) => x.id === 'countroom');
  const first = {
    ...cash,
    screens: [...room, ...cash.screens.filter((x) => x.id !== 'countroom')],
  };
  return [first, ...sections.filter((s) => s !== cash)];
}

/** Rule 1 on the Nav page: the switcher lists only the views a person's capabilities allow. */
export function viewsFor(me: Me): ViewId[] {
  const out = new Set<ViewId>();
  for (const r of me.roles ?? []) for (const v of role(r.role).views) out.add(v);
  return [...out];
}

export function roleName(me: Me): string {
  const r = (me.roles ?? []).map((x) => role(x.role)).sort((a, b) => (a.n ?? 99) - (b.n ?? 99))[0];
  if (me.kind === 'device') return me.device?.label ?? 'Enrolled device';
  return r?.name ?? 'No role yet';
}

export interface Session {
  loading: boolean;
  me: Me | null;
  /** True when the API could not be reached and `me` is the last answer this browser saw. */
  offline: boolean;
  refresh: () => Promise<void>;
}

/** The last `/auth/me` this browser saw — roles and views, never a secret — so the cached shell can say who it was. */
const LAST_ME = 'cider.me';

function remember(me: Me): void {
  try {
    if (me.signedIn) localStorage.setItem(LAST_ME, JSON.stringify(me));
    else localStorage.removeItem(LAST_ME);
  } catch {
    /* private mode: nothing remembered, nothing lost */
  }
}

function remembered(): Me | null {
  try {
    const raw = localStorage.getItem(LAST_ME);
    return raw ? (JSON.parse(raw) as Me) : null;
  } catch {
    return null;
  }
}

/** Asking again while unreachable (Oct 8): 2 s, 5 s, then every 10 s, and at the browser's "online". */
const RETRY_MS = [2_000, 5_000, 10_000];

export function useSession(): Session {
  const [me, setMe] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);
  const [offline, setOffline] = useState(false);
  const refresh = useCallback(async () => {
    const ask = (): Promise<Me> => api<Me>('GET', '/auth/me');
    try {
      let fresh: Me;
      try {
        fresh = await ask();
      } catch (e) {
        // One request lost to a slow start or a blip is not "offline": ask once more first.
        if (!(isRefused(e) && e.refusal.status === 0)) throw e;
        await new Promise((r) => setTimeout(r, 1_500));
        fresh = await ask();
      }
      remember(fresh);
      setMe(fresh);
      setOffline(false);
    } catch (e) {
      // Unreachable: the shell opens on what it last knew and says it is offline. Anything else: signed out.
      const last = isRefused(e) && e.refusal.status === 0 ? remembered() : null;
      setMe(last ?? { signedIn: false });
      setOffline(last !== null);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => {
    void refresh();
  }, [refresh]);
  // Offline is a state to leave, not a verdict for the life of the page: keep asking, and clear
  // the banner the moment the server answers (the four walks of Oct 8 kept one for good).
  useEffect(() => {
    if (!offline) return;
    let step = 0;
    let timer: ReturnType<typeof setTimeout>;
    const again = (): void => {
      timer = setTimeout(
        () => {
          void refresh().finally(() => {
            step += 1;
            again();
          });
        },
        RETRY_MS[Math.min(step, RETRY_MS.length - 1)],
      );
    };
    again();
    const online = (): void => {
      void refresh();
    };
    window.addEventListener('online', online);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('online', online);
    };
  }, [offline, refresh]);
  return { loading, me, refresh, offline };
}
